/**
 * BufferCache.js — Section 5.1
 *
 * Fetches audio from R2 URLs, decodes to AudioBuffer, caches by loop_id.
 * Cache is a Map<loop_id, AudioBuffer>.
 * Pre-fetches all buffers for loaded tracks on project load.
 */

import { getAudioContext } from './AudioEngine';
import { isRenderedClip } from './clipKinds';
import { isAudioEligible } from './classAudio';

const cache = new Map();
const pending = new Map(); // In-flight fetch promises to avoid duplicate requests

/** Store an already-rendered AudioBuffer (for synthesized clips). */
export function putBuffer(loopId, audioBuffer) {
  cache.set(loopId, audioBuffer);
}

export function deleteBuffer(loopId) {
  cache.delete(loopId);
}

/**
 * Fetch, decode, and cache an AudioBuffer for a given loop.
 * Returns the cached buffer if already available.
 *
 * @param {string} loopId - The loop_id key
 * @param {string} audioUrl - The R2 URL to the audio file
 * @returns {Promise<AudioBuffer>}
 */
export async function getBuffer(loopId, audioUrl) {
  // Return from cache if available
  if (cache.has(loopId)) {
    return cache.get(loopId);
  }

  // If already fetching, await the existing promise
  if (pending.has(loopId)) {
    return pending.get(loopId);
  }

  // Fetch, decode, cache
  const promise = fetchAndDecode(loopId, audioUrl);
  pending.set(loopId, promise);

  try {
    const buffer = await promise;
    return buffer;
  } finally {
    pending.delete(loopId);
  }
}

async function fetchAndDecode(loopId, audioUrl) {
  const ctx = getAudioContext();
  const response = await fetch(audioUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch audio for loop ${loopId}: ${response.status}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  cache.set(loopId, audioBuffer);
  return audioBuffer;
}

/**
 * Pre-fetch all AudioBuffers for every clip across all tracks.
 * Call this on project load before allowing playback.
 *
 * @param {Array} tracks - Array of Track objects, each with a clips array
 * @param {Function} getAudioUrl - (loopId) => audioUrl resolver
 * @returns {Promise<void>}
 */
export async function prefetchAll(tracks, getAudioUrl) {
  const uniqueLoops = new Map();
  for (const track of tracks) {
    if (!isAudioEligible(track)) continue;
    for (const clip of track.clips) {
      if (isRenderedClip(clip)) continue;
      if (!uniqueLoops.has(clip.loop_id)) {
        uniqueLoops.set(clip.loop_id, getAudioUrl(clip.loop_id));
      }
    }
  }

  const promises = [];
  for (const [loopId, url] of uniqueLoops) {
    promises.push(getBuffer(loopId, url).catch((err) => {
      console.warn(`BufferCache: failed to prefetch ${loopId}:`, err.message);
    }));
  }

  await Promise.all(promises);
}

/**
 * Check if a buffer is cached.
 */
export function hasCached(loopId) {
  return cache.has(loopId);
}

/**
 * Get the cached buffer directly (returns undefined if not cached).
 */
export function getCached(loopId) {
  return cache.get(loopId);
}

/**
 * Clear the entire cache (e.g., on project change).
 */
export function clearCache() {
  cache.clear();
}

/**
 * Get cache size.
 */
export function cacheSize() {
  return cache.size;
}
